import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon, 
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon 
} from '@heroicons/react/24/outline';
import AnimatedSection from '../components/AnimatedSection';
import emailjs from '@emailjs/browser';


const SERVICE_ID = "service_x4etmhc";            
const TEMPLATE_ID = "template_2b5ew6r";     
const PUBLIC_KEY = "6w2MrjQYp3QANxZmI";     

const API_URL = "https://cleantech-solution.onrender.com";

const Contact = () => {
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    // try {
    //   // Simulate API call
    //   await new Promise(resolve => setTimeout(resolve, 2000));
    try {
    await emailjs.send(
      SERVICE_ID,
      TEMPLATE_ID,
      {
        name: data.name,
        email: data.email,
        company: data.company,
        phone: data.phone,
        service: data.service,
        budget: data.budget,
        message: data.message,
        date: new Date().toLocaleString()
      },
      PUBLIC_KEY
    );

    //   try {
    //   const res = await fetch("http://localhost:8000/api/contact", {
    //   method: "POST",
    //   headers: { "Content-Type": "application/json" },
    //   body: JSON.stringify(data),
    // });
      
      console.log('✅ Email sent!', data);
    setSubmitStatus('success');
    reset();
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error("❌ Email failed:",error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: EnvelopeIcon,
      title: 'Email Us',
      value: 'nahomtemesgen77',
      link: 'mailto:nahomtemesgen77@gmail.com',
      description: 'Send us an email and we\'ll respond within 24 hours'
    },
    {
      icon: PhoneIcon,
      title: 'Call Us',
      value: '+251-93-318-1878',
      link: 'tel:+251933181878',
      description: 'Speak directly with our team during business hours'
    },
    // {
    //   icon: MapPinIcon,
    //   title: 'Visit Us',
    //   value: 'Hawassa, Ethiopia',
    //   link: '#',
    //   description: 'Located in the heart of beautiful City ❤'
    // },
    {
      icon: ClockIcon,
      title: 'Business Hours',
      value: 'Mon-Sat 9AM-6PM PST',
      link: '#',
      description: '24/7 support available for enterprise clients'
    }
  ];

  const services = [
    'Website Development',
    'Mobile App Development',
    'Software Development',
    'Web Application',
    'E-commerce Solution',
    'UI/UX Design',
    'Brand Identity',
    'Digital Marketing',
    'Consulting',
    'Other'
  ];

  const budgetRanges = [
    'Under $2,000',
    '$2,000-$5,000',
    '$5,000 - $15,000',
    '$15,000 - $50,000',
    '$50,000 - $100,000',
    '$100,000+'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-30" />
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-cleantech-white mb-6">
              Let's Create Something <span className="text-gradient">Extraordinary</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 max-w-4xl mx-auto">
              Ready to transform your digital presence? Get in touch with our team and let's discuss 
              how we can bring your vision to life with premium design and cutting-edge technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <div className="glass-card p-8">
                  <h2 className="text-3xl font-bold text-cleantech-white mb-8">
                    Start Your <span className="text-gradient">Project</span>
                  </h2>
                  
                  {submitStatus && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mb-6 p-4 rounded-xl flex items-center ${
                        submitStatus === 'success' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}
                    >
                      {submitStatus === 'success' ? (
                        <CheckCircleIcon className="w-6 h-6 mr-3" />
                      ) : (
                        <ExclamationCircleIcon className="w-6 h-6 mr-3" />
                      )}
                      {submitStatus === 'success' 
                        ? 'Thank you! Your message has been sent successfully. We\'ll get back to you within 24 hours.'
                        : 'Something went wrong. Please try again or contact us directly via email.'
                      }
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Name & Email Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-cleantech-white font-medium mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          {...register('name', { required: 'Name is required' })}
                          className={`w-full ${errors.name ? 'border-red-500' : ''}`}
                          placeholder="Your full name"
                        />
                        {errors.name && (
                          <p className="text-red-400 text-sm mt-1">{errors.name.message}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-cleantech-white font-medium mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          {...register('email', { 
                            required: 'Email is required',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Invalid email address'
                            }
                          })}
                          className={`w-full ${errors.email ? 'border-red-500' : ''}`}
                          placeholder="your@email.com"
                        />
                        {errors.email && (
                          <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Company & Phone Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-cleantech-white font-medium mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          {...register('company')}
                          className="w-full"
                          placeholder="Your company name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-cleantech-white font-medium mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          {...register('phone')}
                          className="w-full"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>

                    {/* Service Interest */}
                    <div>
                      <label className="block text-cleantech-white font-medium mb-2">
                        Service Interest *
                      </label>
                      <select
                        {...register('service', { required: 'Please select a service' })}
                        className={`w-full bg-transparent border-2 border-white/20 rounded-xl px-4 py-3 text-cleantech-white focus:border-cleantech-gold focus:outline-none focus:ring-2 focus:ring-cleantech-gold/30 transition-all duration-300 ${errors.service ? 'border-red-500' : ''}`}
                      >
                        <option value="" className="bg-cleantech-charcoal">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service} className="bg-cleantech-charcoal">
                            {service}
                          </option>
                        ))}
                      </select>
                      {errors.service && (
                        <p className="text-red-400 text-sm mt-1">{errors.service.message}</p>
                      )}
                    </div>

                    {/* Budget Range */}
                    <div>
                      <label className="block text-cleantech-white font-medium mb-2">
                        Project Budget
                      </label>
                      <select
                        {...register('budget')}
                        className="w-full bg-transparent border-2 border-white/20 rounded-xl px-4 py-3 text-cleantech-white focus:border-cleantech-gold focus:outline-none focus:ring-2 focus:ring-cleantech-gold/30 transition-all duration-300"
                      >
                        <option value="" className="bg-cleantech-charcoal">Select budget range</option>
                        {budgetRanges.map((range) => (
                          <option key={range} value={range} className="bg-cleantech-charcoal">
                            {range}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-cleantech-white font-medium mb-2">
                        Project Details *
                      </label>
                      <textarea
                        {...register('message', { required: 'Please describe your project' })}
                        rows={6}
                        className={`w-full resize-none ${errors.message ? 'border-red-500' : ''}`}
                        placeholder="Tell us about your project, goals, and any specific requirements..."
                      />
                      {errors.message && (
                        <p className="text-red-400 text-sm mt-1">{errors.message.message}</p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cleantech-charcoal mr-3"></div>
                          Sending Message...
                        </div>
                      ) : (
                        'Send Message'
                      )}
                    </motion.button>
                  </form>
                </div>
              </AnimatedSection>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <AnimatedSection key={info.title} delay={index * 0.1}>
                    <motion.div
                      whileHover={{ y: -5 }}
                      className="glass-card p-6 hover:shadow-gold transition-all duration-500"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gold-gradient rounded-xl flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-cleantech-charcoal" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-cleantech-white mb-2">{info.title}</h3>
                          <a
                            href={info.link}
                            className="text-cleantech-gold font-medium text-lg hover:text-cleantech-gold-light transition-colors duration-300"
                          >
                            {info.value}
                          </a>
                          <p className="text-white/70 text-sm mt-2">{info.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatedSection>
                );
              })}

              {/* Map Embed */}
              {/* <AnimatedSection delay={0.4}>
                <div className="glass-card overflow-hidden">
                  
                      
                      <iframe
      title="CleanTech Location"
      src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d831.3745598549457!2d38.47821451356803!3d7.048826401040105!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sen!2set!4v1750782679767!5m2!1sen!2set"
      width="100%"
      height="300"
      style={{ border: 0 }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      className="w-full h-64"
    ></iframe>
                   
                </div>
              </AnimatedSection> */}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <AnimatedSection className="py-20 bg-cleantech-charcoal-light">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Quick answers to common questions about our services and process.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {[
              {
                question: "What's included in your website development package?",
                answer: "Our website packages include responsive design, SEO optimization, content management system, hosting setup, SSL certificate, and 3 months of free support and maintenance."
              },
              {
                question: "How long does a typical project take?",
                answer: "Project timelines vary based on complexity. Simple websites take 4-6 weeks, while complex web applications can take 3-6 months. We'll provide a detailed timeline during our initial consultation."
              },
              {
                question: "Do you offer ongoing support and maintenance?",
                answer: "Yes! We provide comprehensive support packages including security updates, content updates, performance monitoring, and technical support. All projects include 3 months of free support."
              },
              {
                question: "Can you work with our existing brand guidelines?",
                answer: "Absolutely! We can work with your existing brand guidelines or help you develop new ones. Our design team is experienced in maintaining brand consistency across all digital touchpoints."
              },
              {
                question: "What's your pricing structure?",
                answer: "Our pricing is project-based and depends on scope, complexity, and requirements. We provide detailed proposals after our initial consultation. Contact us for a custom quote tailored to your needs."
              }
            ].map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card group"
              >
                <summary className="p-6 cursor-pointer text-xl font-semibold text-cleantech-white hover:text-cleantech-gold transition-colors duration-300">
                  {faq.question}
                </summary>
                <div className="px-6 pb-6 text-white/80 border-t border-white/10">
                  <p className="pt-4">{faq.answer}</p>
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default Contact;